import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ComingSoonRibbon } from "../components/ComingSoonRibbon";
import { Pickaxe, ArrowRight, FileText, ShieldCheck, Users, Megaphone, BarChart3, Building2, BookOpen, Mail, Phone, MapPin, Download, Video, Copy, Share2, Building, UserCog } from "lucide-react";

const BANNER = ["/banner/b1.png", "/banner/b2.png", "/banner/b3.png", "/banner/b4.png", "/banner/b5.png"];

const FEATURES = [
  { icon: FileText, title: "Live Opportunities", desc: "RFQ, Tender, RFI & RFP postings from Assmang mines, matched to your district and sector." },
  { icon: ShieldCheck, title: "Compliance Hub", desc: "Upload SARS, BEE & COIDA documents with automatic expiry tracking." },
  { icon: Users, title: "SMME Database", desc: "Buyers discover verified Northern Cape suppliers with powerful filters." },
  { icon: Megaphone, title: "Notice Board", desc: "Meetings, announcements & proposals broadcast to the right suppliers." },
  { icon: BarChart3, title: "Procurement Reports", desc: "Track registrations, opportunities and responses in real time." },
  { icon: Building2, title: "Company Profiles", desc: "Maintain your full company profile and BEE credentials in one place." },
];

function TutorialCard({ vid, copyLink, waHref, testid }) {
  return (
    <div data-testid={`tutorial-card-${vid.id}`} className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      <video data-testid={`tutorial-video-${vid.id}`} src={vid.file} controls preload="metadata"
        className="w-full aspect-video bg-black" />
      <div className="p-4">
        <p className="font-semibold text-slate-900 text-sm">{vid.title}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button data-testid={`tutorial-copy-${vid.id}`} onClick={() => copyLink(vid.file)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00427A] border border-slate-300 hover:border-[#00427A] rounded-md px-3 py-1.5 transition-colors">
            <Copy className="h-3.5 w-3.5" /> Copy link
          </button>
        </div>
      </div>
    </div>
  );
}

function TutorialGroup({ icon: Icon, title, subtitle, vids, copyLink, waHref, testid }) {
  return (
    <div data-testid={`tutorial-group-${testid}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-[#00427A]/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#00427A]" />
        </div>
        <div>
          <h3 className="font-display text-xl font-bold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{subtitle}</p>
        </div>
      </div>
      {vids.length === 0 ? (
        <p className="text-sm text-slate-400">No tutorials yet.</p>
      ) : (
        <div className="grid gap-4">
          {vids.map((v) => <TutorialCard key={v.id} vid={v} copyLink={copyLink} waHref={waHref} testid={testid} />)}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const [slide, setSlide] = useState(0);
  const [tutorials, setTutorials] = useState([]);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % BANNER.length), 4500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    fetch("/tutorials/manifest.json")
      .then((r) => (r.ok ? r.json() : []))
      .then(setTutorials)
      .catch(() => setTutorials([]));
  }, []);

  const smmeTuts = tutorials.filter((v) => v.id.startsWith("smme") || v.id === "getting-started");
  const buyerTuts = tutorials.filter((v) => v.id.startsWith("buyer"));

  const absUrl = (file) => `${window.location.origin}${file}`;
  const copyLink = async (file) => {
    try {
      await navigator.clipboard.writeText(absUrl(file));
      toast.success("Link copied — paste it into your WhatsApp group");
    } catch {
      toast.error("Could not copy link");
    }
  };
  const waHref = (title, file) =>
    `https://wa.me/?text=${encodeURIComponent(`${title} — Assmang SMME Portal tutorial:\n${absUrl(file)}`)}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <ComingSoonRibbon />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="nav-brand-logo">
            <img src="/assmang-logo.png" alt="Assmang logo" className="h-10 w-auto" />
            <span className="hidden sm:inline font-display font-extrabold text-slate-900 tracking-tight">ASSMANG SMME PORTAL</span>
          </div>
          <nav className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <a href="#top" data-testid="nav-tab-home" className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#00427A] transition-colors">Home</a>
            <Link to="/register" data-testid="nav-tab-create-account" className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#00427A] transition-colors">Create Account</Link>
            <a href="#about" data-testid="nav-tab-about" className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#00427A] transition-colors">About</a>
            <a href="#contact" data-testid="nav-tab-contact" className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#00427A] transition-colors">Contact</a>
            <Link to="/login" data-testid="smme-login-button" className="whitespace-nowrap ml-1 px-4 py-2 text-sm font-semibold bg-[#00427A] text-white rounded-md hover:bg-[#00325C] transition-colors">Sign In</Link>
          </nav>
        </div>
      </header>

      <span id="top" />

      <section className="relative overflow-hidden bg-[#0A2540] min-h-[76vh]">
        {BANNER.map((src, i) => (
          <div key={i} data-testid={`banner-slide-${i}`}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${i === slide ? "opacity-100" : "opacity-0"}`}>
            <img src={src} alt={`Assmang operations ${i + 1}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/95 via-[#0A2540]/75 to-[#0A2540]/25" />
          </div>
        ))}
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-[76vh] pt-12 pb-16 flex flex-col justify-center">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#F2B50C] font-semibold mb-5">
            Northern Cape · John Taolo Gaetsewe District · ZF Mgcawu District
          </p>
          <h1 className="font-hero text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-white tracking-tight leading-[1.08] max-w-3xl">
            Supplier & Procurement Portal for Assmang Mining Operations
          </h1>
          <p className="text-lg text-slate-200 mt-6 max-w-2xl">
            Register your SMME, stay compliant, and respond to opportunities at Black Rock, Khumani and Beeshoek mines — all in one trusted platform.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-[#F2B50C] hover:bg-[#D99E00] text-[#0A2540] font-bold rounded-md transition-colors shadow-lg shadow-black/20">
              Get Started <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="/assmang-smme-portal-user-manual.pdf" download data-testid="hero-download-manual"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-md border border-white/25 transition-colors">
              <Download className="h-4 w-4" /> Download User Manual (PDF)
            </a>
          </div>
          <div className="mt-10">
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-slate-300 mb-3">Participating Mines</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-[580px]">
              {["Black Rock Mine Operations", "Khumani Mine", "Beeshoek Mine"].map((name) => (
                <div key={name} data-testid={`mine-card-${name.split(" ")[0].toLowerCase()}`}
                  className="bg-[#A9CBEC] border-2 border-[#0F2A3D] rounded-2xl min-h-[84px] sm:min-h-[96px] px-2 sm:px-3 py-3 flex flex-col items-center justify-center text-center shadow-md">
                  <span className="font-bold text-black text-xs sm:text-base">Assmang</span>
                  <span className="font-bold text-[#E30613] text-xs sm:text-base leading-snug mt-1">{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2.5">
          {BANNER.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} data-testid={`banner-dot-${i}`} aria-label={`Slide ${i + 1}`}
              className={`h-2.5 rounded-full transition-all ${i === slide ? "w-8 bg-[#F2B50C]" : "w-2.5 bg-white/50 hover:bg-white/80"}`} />
          ))}
        </div>
      </section>

      {/* Video Tutorials — immediately below the banner */}
      {tutorials.length > 0 && (
        <section id="tutorials" data-testid="landing-tutorials" className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
            <div className="flex items-center gap-2 mb-2">
              <Video className="h-5 w-5 text-[#F2B50C]" />
              <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#00427A] font-semibold">Video Tutorials</p>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Learn the portal in minutes</h2>
            <p className="text-slate-600 mt-2 max-w-2xl">Narrated step-by-step walkthroughs. Copy a link or share any tutorial straight to your WhatsApp groups.</p>

            <div className="mt-10 grid lg:grid-cols-2 gap-10">
              <TutorialGroup icon={Building} title="SMME Tutorials" subtitle="For suppliers"
                vids={smmeTuts} copyLink={copyLink} waHref={waHref} testid="smme" />
              <TutorialGroup icon={UserCog} title="Buyers Tutorials" subtitle="For mine buyers"
                vids={buyerTuts} copyLink={copyLink} waHref={waHref} testid="buyer" />
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#F2B50C] font-semibold mb-3">Everything you need</p>
        <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tight max-w-2xl">
          One portal for suppliers, buyers and administrators
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-xl border border-slate-200 p-7 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="h-11 w-11 rounded-lg bg-[#00427A]/10 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#00427A]" />
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900">{f.title}</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="about" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#F2B50C] font-semibold mb-3">About the Portal</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Driving local supplier development in the Northern Cape
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              The Assmang SMME Supplier Portal connects small, medium and micro enterprises with procurement
              opportunities across our Black Rock Manganese, Khumani and Beeshoek iron-ore operations. It gives
              suppliers a single place to register, stay compliant, and respond to RFQs, Tenders, RFIs and RFPs —
              while giving mine buyers the tools to discover and engage verified local businesses.
            </p>
          </div>
          <img src="https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
            alt="Business meeting" className="rounded-xl border border-slate-200 w-full h-80 object-cover" />
        </div>
      </section>

      <section id="contact" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#F2B50C] font-semibold mb-3">Get in touch</p>
        <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tight max-w-2xl">Contact Support Team</h2>
        <div className="grid sm:grid-cols-3 gap-6 mt-10">
          {[
            { icon: Mail, title: "Email", value: "Support@assmangsmmeportal.com" },
            { icon: Phone, title: "Telephone", value: "Tel. 053 023 0080 · Cell. 073 8377 509" },
            { icon: MapPin, title: "Address", value: "3 Rose Street, Office No 2, Kuruman" },
          ].map((c) => (
            <div key={c.title} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="h-11 w-11 rounded-lg bg-[#00427A]/10 flex items-center justify-center mb-4">
                <c.icon className="h-5 w-5 text-[#00427A]" />
              </div>
              <h3 className="font-display font-bold text-slate-900">{c.title}</h3>
              <p className="text-slate-600 text-sm mt-1">{c.value}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="bg-[#1E252B] text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Pickaxe className="h-5 w-5 text-[#F2B50C]" />
                <span className="font-display font-bold text-white">ASSMANG SMME PORTAL</span>
              </div>
              <p className="text-sm leading-relaxed">Supplier &amp; procurement portal for Assmang mining operations in the Northern Cape.</p>
            </div>
            <div>
              <h4 className="font-display font-bold text-white mb-4">Contact</h4>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-2.5">
                  <Mail className="h-4 w-4 text-[#F2B50C] mt-0.5 flex-shrink-0" />
                  <a href="mailto:Support@assmangsmmeportal.com" className="hover:text-white transition-colors">Support@assmangsmmeportal.com</a>
                </li>
                <li className="flex items-start gap-2.5">
                  <Phone className="h-4 w-4 text-[#F2B50C] mt-0.5 flex-shrink-0" />
                  <span>Tel. 053 023 0080<br />Cell. 073 8377 509</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold text-white mb-4">Address</h4>
              <div className="flex items-start gap-2.5 text-sm">
                <MapPin className="h-4 w-4 text-[#F2B50C] mt-0.5 flex-shrink-0" />
                <span>3 Rose Street<br />Office No 2<br />Kuruman</span>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6">
            <p className="text-sm">© 2026 Assmang Supplier Development. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
